import { useState, useMemo } from "react"
import {
  Dropdown,
  Spinner,
  Form,
  Button,
  Card,
  Image,
  Alert,
} from "react-bootstrap"
import {
  useAllMainSiteContent,
  useCreateMainSiteContent,
  useDeleteMainSiteContent,
  useUpdateMainSiteContent,
  useToggleMainSiteContentStatus,
} from "../../../api/main-site-content/main-site-content.hooks"
import DeleteModal from "../../components/DeleteModal"
import { useUploadFile } from "../../../api/fileUpload/fileUpload.hooks"
import { FiCopy } from "react-icons/fi"
import Pagination from "../../components/Pagination/Pagination"
import _ from "lodash"
import { standardizePageName } from "../../../utils/stringUtils"
import MetaSection from "./_components/BySiteComponents/MetaSection"

const ByDestination = () => {
  const TYPE = "attractions-by-destination"

  const defaultContentStructure = {
    heroSection: {
      isVisible: true,
      title: "",
      backgroundImage: "",
    },
    princelyStateSection: {
      isVisible: true,
      heading: "",
      description: "",
      image: "",
    },
    topTouristAttractionsSection: {
      isVisible: true,
      heading: "",
      description: "",
      backgroundImage: "",
      link: "",
      attractions: [
        {
          title: "",
          image: "",
          link: "",
        },
      ],
    },
    majesticDarewarFortSection: {
      isVisible: true,
      heading: "",
      description: "",
      mainImage: "",
      link: "",
      sideImages: [{ img: "", name: "", link: "" }],
      featuredCard: {
        title: "",
        description: "",
        image: "",
        link: "",
      },
    },
    colorfulCultureOfCholistanSection: {
      isVisible: true,
      heading: "",
      description: "",
      backgroundImage: "",
      link: "",
      cultureImages: [
        {
          src: "",
          alt: "",
          link: "",
        },
      ],
    },
    festivalsRalliesAndTourismSection: {
      isVisible: true,
      heading: "",
      description: "",
      link: "",
      festivals: [
        {
          title: "",
          city: "",
          description: "",
          image: "",
          link: "",
        },
      ],
    },
    ourHotelsAndResortsSection: {
      isVisible: true,
      heading: "",
      description: "",
      link: "",
      hotels: [
        {
          title: "",
          city: "",
          description: "",
          image: "",
          link: "",
        },
      ],
    },
    meta: {
      isVisible: true,
      title: "",
      description: "",
    },
  }

  const itemsPerPage = 10
  const [currentPage, setCurrentPage] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const [showFormView, setShowFormView] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [formData, setFormData] = useState({
    siteName: "",
    pageName: "",
    type: "",
    content: defaultContentStructure,
  })
  const [selectedItem, setSelectedItem] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [modalAction, setModalAction] = useState('delete') // 'delete' or 'toggle'
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  })
  const [validationErrors, setValidationErrors] = useState({})

  const { upload, isLoading: isUploading } = useUploadFile()
  const { deleteContent, isLoading: isDeleting } = useDeleteMainSiteContent()
  const {
    content: apiContent,
    isLoading,
    refetch,
  } = useAllMainSiteContent({ type: TYPE })
  const { updateContent, isLoading: isUpdating } = useUpdateMainSiteContent()
  const { createContent, isLoading: isCreating } = useCreateMainSiteContent()
  const { toggleStatus, isLoading: isToggling } = useToggleMainSiteContentStatus()

  const showAlert = (message, variant = "info") => {
    setAlert({ show: true, message, variant })
    setTimeout(
      () => setAlert({ show: false, message: "", variant: "info" }),
      5000
    )
  }

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      showAlert("Copied to clipboard!", "success")
    } catch (err) {
      showAlert("Failed to copy!", "danger")
    }
  }

  const handleDelete = async () => {
    if (!selectedItem) return
    try {
      let success = false
      if (modalAction === 'delete') {
        success = await deleteContent(selectedItem._id)
        if (success) {
          showAlert("Item deleted successfully!", "success")
        }
      } else if (modalAction === 'toggle') {
        success = await toggleStatus(selectedItem._id)
        if (success) {
          showAlert("Status toggled successfully!", "success")
        }
      }
      
      if (success) {
        setShowDeleteModal(false)
        await refetch()
      }
    } catch {
      showAlert(`Failed to ${modalAction === 'delete' ? 'delete' : 'toggle status of'} item.`, "danger")
    }
  }

  const handleToggleStatus = async (item) => {
    setSelectedItem(item)
    setModalAction('toggle')
    setShowDeleteModal(true)
  }

  const openDeleteModal = (item) => {
    setSelectedItem(item)
    setModalAction('delete')
    setShowDeleteModal(true)
  }

  const validateForm = () => {
    const errors = {}

    // Validate page name
    if (!formData.pageName.trim()) {
      errors.pageName = "Page name is required"
    }

    // Validate each section
    Object.entries(formData.content).forEach(([section, fields]) => {
      if (fields.isVisible) {
        // Check if section has heading field and it's empty
        if (fields.heading !== undefined && !fields.heading.trim()) {
          errors[`${section}.heading`] =
            "Heading is required when section is visible"
        }

        // Check if section has description field and it's empty
        if (fields.description !== undefined && !fields.description.trim()) {
          errors[`${section}.description`] =
            "Description is required when section is visible"
        }

        // Check if section has title field and it's empty (for heroSection and meta)
        if (fields.title !== undefined && !fields.title.trim()) {
          errors[`${section}.title`] =
            "Title is required when section is visible"
        }
      }
    })

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const sanitizeContentData = (content) => {
    // Create a deep clone of the default structure
    const sanitizedContent = _.cloneDeep(defaultContentStructure)

    // Merge content while maintaining structure
    Object.keys(sanitizedContent).forEach((sectionKey) => {
      if (content[sectionKey]) {
        const section = content[sectionKey]

        // Merge basic fields
        Object.keys(sanitizedContent[sectionKey]).forEach((fieldKey) => {
          if (section[fieldKey] !== undefined) {
            if (Array.isArray(sanitizedContent[sectionKey][fieldKey])) {
              // Handle arrays - validate each item structure
              const defaultArrayItem = sanitizedContent[sectionKey][fieldKey][0]
              if (Array.isArray(section[fieldKey])) {
                sanitizedContent[sectionKey][fieldKey] = section[fieldKey].map(
                  (item) => {
                    if (typeof defaultArrayItem === "object" && defaultArrayItem !== null) {
                      // Ensure each array item has the correct structure
                      const sanitizedItem = {}
                      Object.keys(defaultArrayItem).forEach((itemKey) => {
                        sanitizedItem[itemKey] = item[itemKey] || defaultArrayItem[itemKey]
                      })
                      return sanitizedItem
                    }
                    return item
                  }
                )
              }
            } else if (
              typeof sanitizedContent[sectionKey][fieldKey] === "object" &&
              sanitizedContent[sectionKey][fieldKey] !== null &&
              !Array.isArray(sanitizedContent[sectionKey][fieldKey])
            ) {
              // Handle nested objects (like featuredCard)
              const defaultNestedObj = sanitizedContent[sectionKey][fieldKey]
              if (typeof section[fieldKey] === "object" && section[fieldKey] !== null) {
                const sanitizedNestedObj = {}
                Object.keys(defaultNestedObj).forEach((nestedKey) => {
                  sanitizedNestedObj[nestedKey] = section[fieldKey][nestedKey] || defaultNestedObj[nestedKey]
                })
                sanitizedContent[sectionKey][fieldKey] = sanitizedNestedObj
              }
            } else {
              // Handle primitive values
              sanitizedContent[sectionKey][fieldKey] = section[fieldKey]
            }
          }
        })
      }
    })

    return sanitizedContent
  }

  const handleSaveChanges = async () => {
    if (!validateForm()) {
      showAlert(
        "Please fill in all required fields for visible sections.",
        "danger"
      )
      return
    }

    try {
      // Sanitize content data before sending to API
      const sanitizedContent = sanitizeContentData(formData.content)
      
      const payload = {
        siteName: formData.siteName || "main-site",
        pageName: standardizePageName(formData.pageName), // Standardize page name
        type: TYPE,
        content: sanitizedContent,
      }

      let success = false
      if (editItem) {
        success = await updateContent(editItem._id, payload)
      } else {
        success = await createContent(payload)
      }

      if (success) {
        showAlert(
          editItem ? "Updated successfully!" : "Added successfully!",
          "success"
        )
        setShowFormView(false)
        setValidationErrors({})
        await refetch()
      }
    } catch (err) {
      console.error(err)
      showAlert("Operation failed.", "danger")
    }
  }

  const handleEdit = (item) => {
    setEditItem(item)

    // Sanitize existing content to match default structure
    const sanitizedContent = sanitizeContentData(item.content || {})

    setFormData({
      siteName: item.siteName || "main-site",
      pageName: item.pageName || "",
      type: item.type || TYPE,
      content: sanitizedContent,
    })

    setShowFormView(true)
  }

  const handleInputChange = (section, key, value) => {
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        [section]: { ...prev.content[section], [key]: value },
      },
    }))
  }

  const handleNestedInputChange = (section, nestedKey, key, value) => {
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        [section]: {
          ...prev.content[section],
          [nestedKey]: {
            ...prev.content[section][nestedKey],
            [key]: value,
          },
        },
      },
    }))
  }

  const handleFileUpload = async (section, key, file) => {
    try {
      const res = await upload(file)
      if (res?.url) {
        // Special handling for nested objects like featuredCard
        if (key === "featuredCard") {
          setFormData((prev) => ({
            ...prev,
            content: {
              ...prev.content,
              [section]: {
                ...prev.content[section],
                featuredCard: {
                  ...prev.content[section].featuredCard,
                  image: res.url,
                },
              },
            },
          }))
        } else {
          handleInputChange(section, key, res.url)
        }
      }
    } catch (err) {
      showAlert("File upload failed.", "danger")
    }
  }

  // Attractions handlers
  const handleAttractionsChange = (index, field, value) => {
    const attractions = [
      ...formData.content.topTouristAttractionsSection.attractions,
    ]
    attractions[index][field] = value
    handleInputChange(
      "topTouristAttractionsSection",
      "attractions",
      attractions
    )
  }

  const handleAddAttraction = () => {
    const attractions = [
      ...formData.content.topTouristAttractionsSection.attractions,
    ]
    // if (attractions.length >= 4) return
    attractions.push({ title: "", image: "", link: "" })
    handleInputChange(
      "topTouristAttractionsSection",
      "attractions",
      attractions
    )
  }

  const handleRemoveAttraction = (index) => {
    const attractions = [
      ...formData.content.topTouristAttractionsSection.attractions,
    ]
    attractions.splice(index, 1)
    handleInputChange(
      "topTouristAttractionsSection",
      "attractions",
      attractions
    )
  }

  const handleAttractionImageUpload = async (index, file) => {
    try {
      const res = await upload(file)
      if (res?.url) {
        const attractions = [
          ...formData.content.topTouristAttractionsSection.attractions,
        ]
        attractions[index].image = res.url
        handleInputChange(
          "topTouristAttractionsSection",
          "attractions",
          attractions
        )
      }
    } catch {
      showAlert("Failed to upload image.", "danger")
    }
  }

  // Culture images handlers
  const handleCultureImagesChange = (index, field, value) => {
    const cultureImages = [
      ...formData.content.colorfulCultureOfCholistanSection.cultureImages,
    ]
    cultureImages[index][field] = value
    handleInputChange(
      "colorfulCultureOfCholistanSection",
      "cultureImages",
      cultureImages
    )
  }

  const handleAddCultureImage = () => {
    const cultureImages = [
      ...formData.content.colorfulCultureOfCholistanSection.cultureImages,
    ]
    // if (cultureImages.length >= 4) return
    cultureImages.push({ src: "", alt: "", link: "" })
    handleInputChange(
      "colorfulCultureOfCholistanSection",
      "cultureImages",
      cultureImages
    )
  }

  const handleRemoveCultureImage = (index) => {
    const cultureImages = [
      ...formData.content.colorfulCultureOfCholistanSection.cultureImages,
    ]
    cultureImages.splice(index, 1)
    handleInputChange(
      "colorfulCultureOfCholistanSection",
      "cultureImages",
      cultureImages
    )
  }

  const handleCultureImageUpload = async (index, file) => {
    try {
      const res = await upload(file)
      if (res?.url) {
        const cultureImages = [
          ...formData.content.colorfulCultureOfCholistanSection.cultureImages,
        ]
        cultureImages[index].src = res.url
        handleInputChange(
          "colorfulCultureOfCholistanSection",
          "cultureImages",
          cultureImages
        )
      }
    } catch {
      showAlert("Failed to upload image.", "danger")
    }
  }

  // Festivals handlers
  const handleFestivalsChange = (index, field, value) => {
    const festivals = [
      ...formData.content.festivalsRalliesAndTourismSection.festivals,
    ]
    festivals[index][field] = value
    handleInputChange(
      "festivalsRalliesAndTourismSection",
      "festivals",
      festivals
    )
  }

  const handleAddFestival = () => {
    const festivals = [
      ...formData.content.festivalsRalliesAndTourismSection.festivals,
    ]
    // if (festivals.length >= 4) return
    festivals.push({
      title: "",
      city: "",
      description: "",
      image: "",
      link: "",
    })
    handleInputChange(
      "festivalsRalliesAndTourismSection",
      "festivals",
      festivals
    )
  }

  const handleRemoveFestival = (index) => {
    const festivals = [
      ...formData.content.festivalsRalliesAndTourismSection.festivals,
    ]
    festivals.splice(index, 1)
    handleInputChange(
      "festivalsRalliesAndTourismSection",
      "festivals",
      festivals
    )
  }

  const handleFestivalImageUpload = async (index, file) => {
    try {
      const res = await upload(file)
      if (res?.url) {
        const festivals = [
          ...formData.content.festivalsRalliesAndTourismSection.festivals,
        ]
        festivals[index].image = res.url
        handleInputChange(
          "festivalsRalliesAndTourismSection",
          "festivals",
          festivals
        )
      }
    } catch {
      showAlert("Failed to upload image.", "danger")
    }
  }

  // Hotels handlers
  const handleHotelsChange = (index, field, value) => {
    const hotels = [...formData.content.ourHotelsAndResortsSection.hotels]
    hotels[index][field] = value
    handleInputChange("ourHotelsAndResortsSection", "hotels", hotels)
  }

  const handleAddHotel = () => {
    const hotels = [...formData.content.ourHotelsAndResortsSection.hotels]
    // if (hotels.length >= 4) return
    hotels.push({ title: "", city: "", description: "", image: "", link: "" })
    handleInputChange("ourHotelsAndResortsSection", "hotels", hotels)
  }

  const handleRemoveHotel = (index) => {
    const hotels = [...formData.content.ourHotelsAndResortsSection.hotels]
    hotels.splice(index, 1)
    handleInputChange("ourHotelsAndResortsSection", "hotels", hotels)
  }

  const handleHotelImageUpload = async (index, file) => {
    try {
      const res = await upload(file)
      if (res?.url) {
        const hotels = [...formData.content.ourHotelsAndResortsSection.hotels]
        hotels[index].image = res.url
        handleInputChange("ourHotelsAndResortsSection", "hotels", hotels)
      }
    } catch {
      showAlert("Failed to upload image.", "danger")
    }
  }

  // Side images handlers
  const handleAddSideImage = () => {
    const sideImages = [
      ...formData.content.majesticDarewarFortSection.sideImages,
    ]
    // if (sideImages.length >= 4) return
    sideImages.push({ img: "", name: "", link: "" })
    handleInputChange("majesticDarewarFortSection", "sideImages", sideImages)
  }

  const handleSideImageChange = (index, field, value) => {
    const sideImages = [
      ...formData.content.majesticDarewarFortSection.sideImages,
    ]
    sideImages[index][field] = value
    handleInputChange("majesticDarewarFortSection", "sideImages", sideImages)
  }

  const handleSideImageUpload = async (index, file) => {
    try {
      const res = await upload(file)
      if (res?.url) {
        const sideImages = [
          ...formData.content.majesticDarewarFortSection.sideImages,
        ]
        sideImages[index].img = res.url
        handleInputChange(
          "majesticDarewarFortSection",
          "sideImages",
          sideImages
        )
      }
    } catch {
      showAlert("Failed to upload image.", "danger")
    }
  }

  const handleRemoveSideImage = (index) => {
    const sideImages = [
      ...formData.content.majesticDarewarFortSection.sideImages,
    ]
    sideImages.splice(index, 1)
    handleInputChange("majesticDarewarFortSection", "sideImages", sideImages)
  }

  const tableData = useMemo(() => {
    if (!Array.isArray(apiContent)) return []
    return apiContent.map((item) => ({
      id: item._id || item.id,
      siteName: item.siteName || "-",
      pageName: item.pageName || "-",
      type: item.type || "-",
      isActive: item.isActive,
      fullItem: item,
    }))
  }, [apiContent])

  const filtered = tableData.filter((x) =>
    [x.siteName, x.pageName, x.type]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  )

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1
  const paginated = filtered.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  )

  return (
    <div className='col-12'>
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() =>
            setAlert({ show: false, message: "", variant: "info" })
          }
          className='mb-3'
        >
          {alert.message}
        </Alert>
      )}

      {!showFormView ? (
        <div className='card'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <h4 className='card-title m-0'>By Destination</h4>
            <div className='d-flex gap-2'>
              <input
                type='text'
                className='form-control w-auto'
                placeholder='Search content'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button
                onClick={() => {
                  setEditItem(null)
                  setFormData({
                    siteName: "main-site",
                    pageName: "",
                    type: TYPE,
                    content: defaultContentStructure,
                  })
                  setShowFormView(true)
                }}
              >
                + Add
              </Button>
            </div>
          </div>

          <div className='card-body'>
            {isLoading ? (
              <div className='d-flex justify-content-center py-5'>
                <Spinner animation='border' />
              </div>
            ) : (
              <>
                <div className='table-responsive'>
                  <table className='table table-striped'>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Site</th>
                        <th>Page</th>
                        <th>Url</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((item, i) => (
                        <tr key={item.id}>
                          <td>{currentPage * itemsPerPage + i + 1}</td>
                          <td>{item.siteName}</td>
                          <td>{item.pageName}</td>
                          <td className='d-flex align-items-center gap-2'>
                            <span>/districts/{item.pageName}</span>
                            <Button
                              variant='link'
                              onClick={() =>
                                handleCopy(`/districts/${item.pageName}`)
                              }
                              title='Copy URL'
                            >
                              <FiCopy />
                            </Button>
                          </td>
                          <td>
                            <span className={`badge ${item.isActive ? 'bg-success' : 'bg-secondary'}`}>
                              {item.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle variant='link'>
                                &#x2022;&#x2022;&#x2022;
                              </Dropdown.Toggle>
                              <Dropdown.Menu align='end'>
                                <Dropdown.Item
                                  onClick={() => handleEdit(item.fullItem)}
                                >
                                  Edit
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleToggleStatus(item.fullItem)}
                                >
                                  {item.fullItem.isActive ? 'Deactivate' : 'Activate'}
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => openDeleteModal(item.fullItem)}
                                  className="text-danger"
                                >
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filtered.length}
                  onPageChange={setCurrentPage}
                />

                {filtered.length === 0 && !isLoading && (
                  <div className="text-center py-4">
                    <p className="text-muted">No content found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className='card p-4'>
          <div className='d-flex align-items-center mb-4'>
            <Button
              variant='link'
              className='p-0 me-3'
              onClick={() => setShowFormView(false)}
            >
              ← Back
            </Button>
            <h4 className='m-0'>
              {editItem ? "Edit Content" : "Add New Content"}
            </h4>
          </div>

          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>
                Page Name <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                type='text'
                value={formData.pageName}
                onChange={(e) => {
                  setFormData({ ...formData, pageName: e.target.value })
                  if (validationErrors.pageName) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      pageName: undefined,
                    }))
                  }
                }}
                isInvalid={!!validationErrors.pageName}
              />
              {formData.pageName && (
                <Form.Text className="text-muted">
                  URL Preview: /districts/{standardizePageName(formData.pageName)}
                </Form.Text>
              )}
              {validationErrors.pageName && (
                <Form.Control.Feedback type='invalid'>
                  {validationErrors.pageName}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {Object.entries(formData.content)
              .filter(([section]) => section !== "meta")
              .map(([section, fields]) => (
              <Card key={section} className='mb-4 shadow'>
                <Card.Header className='bg-neutral-gray text-dark d-flex justify-content-between align-items-center'>
                  <h5 className='mb-0 text-capitalize text-dark'>
                    {section === "heroSection" && "Hero Section"}
                    {section === "princelyStateSection" &&
                      "About Section"}
                    {section === "topTouristAttractionsSection" &&
                      "Section 1"}
                    {section === "majesticDarewarFortSection" &&
                      "Section 2"}
                    {section === "colorfulCultureOfCholistanSection" &&
                      "Section 3"}
                    {section === "festivalsRalliesAndTourismSection" &&
                      "Section 4"}
                    {section === "ourHotelsAndResortsSection" &&
                      "Section 5"}
                    {section === "meta" && "Meta Information"}
                  </h5>
                  <Form.Check
                    type='checkbox'
                    label='Visible'
                    checked={fields.isVisible}
                    onChange={(e) =>
                      handleInputChange(section, "isVisible", e.target.checked)
                    }
                    className='mb-0'
                  />
                </Card.Header>
                {fields.isVisible && (
                  <Card.Body>
                    <>
                      {section === "topTouristAttractionsSection" ? (
                        <>
                          <Form.Control
                            className='mb-3'
                            placeholder={`Heading ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.heading}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "heading",
                                e.target.value
                              )
                              if (validationErrors[`${section}.heading`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.heading`]: undefined,
                                }))
                              }
                            }}
                            isInvalid={!!validationErrors[`${section}.heading`]}
                          />
                          {validationErrors[`${section}.heading`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.heading`]}
                            </div>
                          )}
                          <Form.Control
                            as='textarea'
                            rows={2}
                            placeholder={`Description ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.description}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "description",
                                e.target.value
                              )
                              if (validationErrors[`${section}.description`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.description`]: undefined,
                                }))
                              }
                            }}
                            className='mb-4'
                            isInvalid={
                              !!validationErrors[`${section}.description`]
                            }
                          />
                          <Form.Control
                            className='mb-2'
                            placeholder='Link (Optional)'
                            value={fields.link}
                            onChange={(e) =>
                              handleInputChange(section, "link", e.target.value)
                            }
                          />

                          <Form.Label>Background Image</Form.Label>
                          <Form.Control
                            type='file'
                            accept='image/*'
                            onChange={(e) =>
                              e.target.files?.[0] &&
                              handleFileUpload(
                                section,
                                "backgroundImage",
                                e.target.files[0]
                              )
                            }
                          />
                          {fields.backgroundImage && (
                            <div className='mt-2 mb-3'>
                              <Image
                                src={fields.backgroundImage}
                                thumbnail
                                style={{ maxWidth: "200px" }}
                              />
                            </div>
                          )}

                          <h6 className='fw-bold'>Attractions</h6>
                          {fields.attractions.map((attraction, idx) => (
                            <div
                              key={idx}
                              className='border rounded p-3 mb-3 bg-light-subtle'
                            >
                              <Form.Control
                                className='mb-2'
                                placeholder='Title'
                                value={attraction.title}
                                onChange={(e) =>
                                  handleAttractionsChange(
                                    idx,
                                    "title",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Control
                                className='mb-2'
                                placeholder='Link (Optional)'
                                value={attraction.link}
                                onChange={(e) =>
                                  handleAttractionsChange(
                                    idx,
                                    "link",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Label>Image</Form.Label>
                              <Form.Control
                                type='file'
                                accept='image/*'
                                onChange={(e) =>
                                  e.target.files?.[0] &&
                                  handleAttractionImageUpload(
                                    idx,
                                    e.target.files[0]
                                  )
                                }
                              />
                              {isUploading && (
                                <div className='text-muted small mt-1'>
                                  Uploading...
                                </div>
                              )}
                              {attraction.image && (
                                <div className='mt-2'>
                                  <Image
                                    src={attraction.image}
                                    thumbnail
                                    style={{ maxWidth: "200px" }}
                                  />
                                </div>
                              )}
                              <div className='text-end mt-2'>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  onClick={() => handleRemoveAttraction(idx)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant='outline-primary'
                            size='sm'
                            onClick={handleAddAttraction}
                            // disabled={fields.attractions.length >= 4}
                          >
                            + Add Attraction
                          </Button>
                        </>
                      ) : section === "majesticDarewarFortSection" ? (
                        <>
                          <Form.Control
                            className='mb-3'
                            placeholder={`Heading ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.heading}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "heading",
                                e.target.value
                              )
                              if (validationErrors[`${section}.heading`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.heading`]: undefined,
                                }))
                              }
                            }}
                            isInvalid={!!validationErrors[`${section}.heading`]}
                          />
                          {validationErrors[`${section}.heading`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.heading`]}
                            </div>
                          )}
                          <Form.Control
                            as='textarea'
                            rows={2}
                            placeholder={`Description ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.description}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "description",
                                e.target.value
                              )
                              if (validationErrors[`${section}.description`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.description`]: undefined,
                                }))
                              }
                            }}
                            className='mb-4'
                            isInvalid={
                              !!validationErrors[`${section}.description`]
                            }
                          />
                          {validationErrors[`${section}.description`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.description`]}
                            </div>
                          )}

                          <Form.Control
                            className='mb-2'
                            placeholder='Link (Optional)'
                            value={fields.link}
                            onChange={(e) =>
                              handleInputChange(section, "link", e.target.value)
                            }
                          />

                          <Form.Label>Main Image</Form.Label>
                          <Form.Control
                            type='file'
                            accept='image/*'
                            onChange={(e) =>
                              e.target.files?.[0] &&
                              handleFileUpload(
                                section,
                                "mainImage",
                                e.target.files[0]
                              )
                            }
                          />
                          {fields.mainImage && (
                            <div className='mt-2 mb-3'>
                              <Image
                                src={fields.mainImage}
                                thumbnail
                                style={{ maxWidth: "200px" }}
                              />
                            </div>
                          )}

                          <h6 className='fw-bold'>Side Images</h6>
                          {fields.sideImages.map((sideImg, idx) => (
                            <div
                              key={idx}
                              className='border rounded p-3 mb-3 bg-light-subtle'
                            >
                              <Form.Control
                                className='mb-2'
                                placeholder='Name'
                                value={sideImg.name}
                                onChange={(e) =>
                                  handleSideImageChange(
                                    idx,
                                    "name",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Control
                                className='mb-2'
                                placeholder='Link (Optional)'
                                value={sideImg.link || ""}
                                onChange={(e) =>
                                  handleSideImageChange(
                                    idx,
                                    "link",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Label>Side Image {idx + 1}</Form.Label>
                              <Form.Control
                                type='file'
                                accept='image/*'
                                onChange={(e) =>
                                  e.target.files?.[0] &&
                                  handleSideImageUpload(idx, e.target.files[0])
                                }
                              />
                              {isUploading && (
                                <div className='text-muted small mt-1'>
                                  Uploading...
                                </div>
                              )}
                              {sideImg.img && (
                                <div className='mt-2'>
                                  <Image
                                    src={sideImg.img}
                                    thumbnail
                                    style={{ maxWidth: "200px" }}
                                  />
                                </div>
                              )}
                              <div className='text-end mt-2'>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  onClick={() => handleRemoveSideImage(idx)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant='outline-primary'
                            size='sm'
                            onClick={handleAddSideImage}
                            // disabled={fields.sideImages.length >= 4}
                          >
                            + Add Side Image
                          </Button>

                          <h6 className='fw-bold mt-2'>Featured Card</h6>
                          <Form.Control
                            className='mb-2'
                            placeholder='Featured Card Title'
                            value={fields.featuredCard?.title || ""}
                            onChange={(e) =>
                              handleNestedInputChange(
                                section,
                                "featuredCard",
                                "title",
                                e.target.value
                              )
                            }
                          />
                          <Form.Control
                            as='textarea'
                            rows={2}
                            placeholder='Featured Card Description'
                            value={fields.featuredCard?.description || ""}
                            onChange={(e) =>
                              handleNestedInputChange(
                                section,
                                "featuredCard",
                                "description",
                                e.target.value
                              )
                            }
                            className='mb-2'
                          />
                          <Form.Control
                            className='mb-2'
                            placeholder='Featured Card Link (Optional)'
                            value={fields.featuredCard?.link || ""}
                            onChange={(e) =>
                              handleNestedInputChange(
                                section,
                                "featuredCard",
                                "link",
                                e.target.value
                              )
                            }
                          />
                          <Form.Label>Featured Card Image</Form.Label>
                          <Form.Control
                            type='file'
                            accept='image/*'
                            onChange={(e) =>
                              e.target.files?.[0] &&
                              handleFileUpload(
                                section,
                                "featuredCard",
                                e.target.files[0]
                              )
                            }
                          />
                          {isUploading && (
                            <div className='text-muted small mt-1'>
                              Uploading...
                            </div>
                          )}
                          {fields.featuredCard?.image && (
                            <div className='mt-2 mb-3'>
                              <Image
                                src={fields.featuredCard.image}
                                thumbnail
                                style={{ maxWidth: "200px" }}
                              />
                            </div>
                          )}
                        </>
                      ) : section === "colorfulCultureOfCholistanSection" ? (
                        <>
                          <Form.Control
                            className='mb-3'
                            placeholder={`Heading ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.heading}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "heading",
                                e.target.value
                              )
                              if (validationErrors[`${section}.heading`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.heading`]: undefined,
                                }))
                              }
                            }}
                            isInvalid={!!validationErrors[`${section}.heading`]}
                          />
                          {validationErrors[`${section}.heading`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.heading`]}
                            </div>
                          )}
                          <Form.Control
                            as='textarea'
                            rows={2}
                            placeholder={`Description ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.description}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "description",
                                e.target.value
                              )
                              if (validationErrors[`${section}.description`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.description`]: undefined,
                                }))
                              }
                            }}
                            className='mb-4'
                            isInvalid={
                              !!validationErrors[`${section}.description`]
                            }
                          />
                          {validationErrors[`${section}.description`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.description`]}
                            </div>
                          )}

                          <Form.Control
                            className='mb-2'
                            placeholder='Link (Optional)'
                            value={fields.link}
                            onChange={(e) =>
                              handleInputChange(section, "link", e.target.value)
                            }
                          />

                          <Form.Label>Background Image</Form.Label>
                          <Form.Control
                            type='file'
                            accept='image/*'
                            onChange={(e) =>
                              e.target.files?.[0] &&
                              handleFileUpload(
                                section,
                                "backgroundImage",
                                e.target.files[0]
                              )
                            }
                          />
                          {fields.backgroundImage && (
                            <div className='mt-2 mb-3'>
                              <Image
                                src={fields.backgroundImage}
                                thumbnail
                                style={{ maxWidth: "200px" }}
                              />
                            </div>
                          )}

                          <h6 className='fw-bold'>Culture Images</h6>
                          {fields.cultureImages.map((img, idx) => (
                            <div
                              key={idx}
                              className='border rounded p-3 mb-3 bg-light-subtle'
                            >
                              <Form.Control
                                className='mb-2'
                                placeholder='Alt Text'
                                value={img.alt}
                                onChange={(e) =>
                                  handleCultureImagesChange(
                                    idx,
                                    "alt",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Control
                                className='mb-2'
                                placeholder='Link (Optional)'
                                value={img.link}
                                onChange={(e) =>
                                  handleCultureImagesChange(
                                    idx,
                                    "link",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Label>Image</Form.Label>
                              <Form.Control
                                type='file'
                                accept='image/*'
                                onChange={(e) =>
                                  e.target.files?.[0] &&
                                  handleCultureImageUpload(
                                    idx,
                                    e.target.files[0]
                                  )
                                }
                              />
                              {isUploading && (
                                <div className='text-muted small mt-1'>
                                  Uploading...
                                </div>
                              )}
                              {img.src && (
                                <div className='mt-2'>
                                  <Image
                                    src={img.src}
                                    thumbnail
                                    style={{ maxWidth: "200px" }}
                                  />
                                </div>
                              )}
                              <div className='text-end mt-2'>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  onClick={() => handleRemoveCultureImage(idx)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant='outline-primary'
                            size='sm'
                            onClick={handleAddCultureImage}
                            // disabled={fields.cultureImages.length >= 4}
                          >
                            + Add Culture Image
                          </Button>
                        </>
                      ) : section === "festivalsRalliesAndTourismSection" ? (
                        <>
                          <Form.Control
                            className='mb-3'
                            placeholder={`Heading ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.heading}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "heading",
                                e.target.value
                              )
                              if (validationErrors[`${section}.heading`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.heading`]: undefined,
                                }))
                              }
                            }}
                            isInvalid={!!validationErrors[`${section}.heading`]}
                          />
                          {validationErrors[`${section}.heading`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.heading`]}
                            </div>
                          )}
                          <Form.Control
                            as='textarea'
                            rows={2}
                            placeholder={`Description ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.description}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "description",
                                e.target.value
                              )
                              if (validationErrors[`${section}.description`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.description`]: undefined,
                                }))
                              }
                            }}
                            className='mb-4'
                            isInvalid={
                              !!validationErrors[`${section}.description`]
                            }
                          />
                          {validationErrors[`${section}.description`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.description`]}
                            </div>
                          )}

                          <Form.Control
                            className='mb-2'
                            placeholder='Link (Optional)'
                            value={fields.link}
                            onChange={(e) =>
                              handleInputChange(section, "link", e.target.value)
                            }
                          />

                          <h6 className='fw-bold'>Festivals</h6>
                          {fields.festivals.map((festival, idx) => (
                            <div
                              key={idx}
                              className='border rounded p-3 mb-3 bg-light-subtle'
                            >
                              <Form.Control
                                className='mb-2'
                                placeholder='Title'
                                value={festival.title}
                                onChange={(e) =>
                                  handleFestivalsChange(
                                    idx,
                                    "title",
                                    e.target.value
                                  )
                                }
                              />
                              <Form.Control
                                className='mb-2'
                                placeholder='City'
                                value={festival.city}
                                onChange={(e) =>
                                  handleFestivalsChange(
                                    idx,
                                    "city",
                                    e.target.value
                                  )
                                }
                              />
                              <Form.Control
                                as='textarea'
                                rows={2}
                                placeholder='Description'
                                value={festival.description}
                                onChange={(e) =>
                                  handleFestivalsChange(
                                    idx,
                                    "description",
                                    e.target.value
                                  )
                                }
                                className='mb-2'
                              />

                              <Form.Control
                                className='mb-2'
                                placeholder='Link (Optional)'
                                value={festival.link}
                                onChange={(e) =>
                                  handleFestivalsChange(
                                    idx,
                                    "link",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Label>Image</Form.Label>
                              <Form.Control
                                type='file'
                                accept='image/*'
                                onChange={(e) =>
                                  e.target.files?.[0] &&
                                  handleFestivalImageUpload(
                                    idx,
                                    e.target.files[0]
                                  )
                                }
                              />
                              {isUploading && (
                                <div className='text-muted small mt-1'>
                                  Uploading...
                                </div>
                              )}
                              {festival.image && (
                                <div className='mt-2'>
                                  <Image
                                    src={festival.image}
                                    thumbnail
                                    style={{ maxWidth: "200px" }}
                                  />
                                </div>
                              )}
                              <div className='text-end mt-2'>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  onClick={() => handleRemoveFestival(idx)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant='outline-primary'
                            size='sm'
                            onClick={handleAddFestival}
                            // disabled={fields.festivals.length >= 4}
                          >
                            + Add Festival
                          </Button>
                        </>
                      ) : section === "ourHotelsAndResortsSection" ? (
                        <>
                          <Form.Control
                            className='mb-3'
                            placeholder={`Heading ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.heading}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "heading",
                                e.target.value
                              )
                              if (validationErrors[`${section}.heading`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.heading`]: undefined,
                                }))
                              }
                            }}
                            isInvalid={!!validationErrors[`${section}.heading`]}
                          />
                          {validationErrors[`${section}.heading`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.heading`]}
                            </div>
                          )}
                          <Form.Control
                            as='textarea'
                            rows={2}
                            placeholder={`Description ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.description}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "description",
                                e.target.value
                              )
                              if (validationErrors[`${section}.description`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.description`]: undefined,
                                }))
                              }
                            }}
                            className='mb-4'
                            isInvalid={
                              !!validationErrors[`${section}.description`]
                            }
                          />
                          {validationErrors[`${section}.description`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.description`]}
                            </div>
                          )}

                          <Form.Control
                            className='mb-2'
                            placeholder='Link (Optional)'
                            value={fields.link}
                            onChange={(e) =>
                              handleInputChange(section, "link", e.target.value)
                            }
                          />

                          <h6 className='fw-bold'>Hotels</h6>
                          {fields.hotels.map((hotel, idx) => (
                            <div
                              key={idx}
                              className='border rounded p-3 mb-3 bg-light-subtle'
                            >
                              <Form.Control
                                className='mb-2'
                                placeholder='Title'
                                value={hotel.title}
                                onChange={(e) =>
                                  handleHotelsChange(
                                    idx,
                                    "title",
                                    e.target.value
                                  )
                                }
                              />
                              <Form.Control
                                className='mb-2'
                                placeholder='City'
                                value={hotel.city}
                                onChange={(e) =>
                                  handleHotelsChange(
                                    idx,
                                    "city",
                                    e.target.value
                                  )
                                }
                              />
                              <Form.Control
                                as='textarea'
                                rows={2}
                                placeholder='Description'
                                value={hotel.description}
                                onChange={(e) =>
                                  handleHotelsChange(
                                    idx,
                                    "description",
                                    e.target.value
                                  )
                                }
                                className='mb-2'
                              />

                              <Form.Control
                                className='mb-2'
                                placeholder='Link (Optional)'
                                value={hotel.link}
                                onChange={(e) =>
                                  handleHotelsChange(
                                    idx,
                                    "link",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Label>Image</Form.Label>
                              <Form.Control
                                type='file'
                                accept='image/*'
                                onChange={(e) =>
                                  e.target.files?.[0] &&
                                  handleHotelImageUpload(idx, e.target.files[0])
                                }
                              />
                              {isUploading && (
                                <div className='text-muted small mt-1'>
                                  Uploading...
                                </div>
                              )}
                              {hotel.image && (
                                <div className='mt-2'>
                                  <Image
                                    src={hotel.image}
                                    thumbnail
                                    style={{ maxWidth: "200px" }}
                                  />
                                </div>
                              )}
                              <div className='text-end mt-2'>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  onClick={() => handleRemoveHotel(idx)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant='outline-primary'
                            size='sm'
                            onClick={handleAddHotel}
                            // disabled={fields.hotels.length >= 4}
                          >
                            + Add Hotel
                          </Button>
                        </>
                      ) : (
                        Object.entries(fields).map(([key, value]) => {
                          if (key === "isVisible") return null // Skip isVisible as it's now in header

                          const isImageField =
                            key.toLowerCase().includes("image") ||
                            key.toLowerCase().includes("thumbnail")
                          const isRequiredField =
                            fields.isVisible &&
                            (key === "heading" ||
                              key === "description" ||
                              key === "title")

                          if (typeof value === "boolean") {
                            return (
                              <Form.Check
                                key={key}
                                type='checkbox'
                                label={key}
                                checked={value}
                                onChange={(e) =>
                                  handleInputChange(
                                    section,
                                    key,
                                    e.target.checked
                                  )
                                }
                                className='mb-3'
                              />
                            )
                          } else if (isImageField) {
                            return (
                              <div key={key} className='mb-3'>
                                <Form.Label className='text-capitalize'>
                                  {key}
                                </Form.Label>
                                <Form.Control
                                  type='file'
                                  accept='image/*'
                                  onChange={(e) =>
                                    e.target.files?.[0] &&
                                    handleFileUpload(
                                      section,
                                      key,
                                      e.target.files[0]
                                    )
                                  }
                                />
                                {isUploading && (
                                  <div className='text-muted small mt-1'>
                                    Uploading...
                                  </div>
                                )}
                                {value && (
                                  <div className='mt-2'>
                                    <Image
                                      src={value}
                                      alt='Preview'
                                      thumbnail
                                      style={{ maxWidth: "200px" }}
                                    />
                                  </div>
                                )}
                              </div>
                            )
                          } else if (key === "description") {
                            return (
                              <Form.Group key={key} className='mb-3'>
                                <Form.Label className='text-capitalize'>
                                  {key}{" "}
                                  {isRequiredField && (
                                    <span className='text-danger'>*</span>
                                  )}
                                </Form.Label>
                                <Form.Control
                                  as='textarea'
                                  rows={2}
                                  placeholder={`${key} ${
                                    isRequiredField ? "*" : ""
                                  }`}
                                  value={value}
                                  onChange={(e) => {
                                    handleInputChange(
                                      section,
                                      key,
                                      e.target.value
                                    )
                                    if (validationErrors[`${section}.${key}`]) {
                                      setValidationErrors((prev) => ({
                                        ...prev,
                                        [`${section}.${key}`]: undefined,
                                      }))
                                    }
                                  }}
                                  isInvalid={
                                    !!validationErrors[`${section}.${key}`]
                                  }
                                />
                                {validationErrors[`${section}.${key}`] && (
                                  <Form.Control.Feedback type='invalid'>
                                    {validationErrors[`${section}.${key}`]}
                                  </Form.Control.Feedback>
                                )}
                              </Form.Group>
                            )
                          } else if (key === "link") {
                            return (
                              <Form.Group key={key} className='mb-3'>
                                <Form.Label className='text-capitalize'>
                                  {key} (Optional)
                                </Form.Label>
                                <Form.Control
                                  type='url'
                                  placeholder='/sites/site-name'
                                  value={value}
                                  onChange={(e) =>
                                    handleInputChange(
                                      section,
                                      key,
                                      e.target.value
                                    )
                                  }
                                />
                              </Form.Group>
                            )
                          } else {
                            return (
                              <Form.Group key={key} className='mb-3'>
                                <Form.Label className='text-capitalize'>
                                  {key}{" "}
                                  {isRequiredField && (
                                    <span className='text-danger'>*</span>
                                  )}
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  placeholder={`${key} ${
                                    isRequiredField ? "*" : ""
                                  }`}
                                  value={value}
                                  onChange={(e) => {
                                    handleInputChange(
                                      section,
                                      key,
                                      e.target.value
                                    )
                                    if (validationErrors[`${section}.${key}`]) {
                                      setValidationErrors((prev) => ({
                                        ...prev,
                                        [`${section}.${key}`]: undefined,
                                      }))
                                    }
                                  }}
                                  isInvalid={
                                    !!validationErrors[`${section}.${key}`]
                                  }
                                />
                                {validationErrors[`${section}.${key}`] && (
                                  <Form.Control.Feedback type='invalid'>
                                    {validationErrors[`${section}.${key}`]}
                                  </Form.Control.Feedback>
                                )}
                              </Form.Group>
                            )
                          }
                        })
                      )}
                    </>
                  </Card.Body>
                )}
              </Card>
            ))}

            {/* Meta Section */}
            <MetaSection
              data={formData.content.meta}
              onUpdate={(metaData) =>
                setFormData((prev) => ({
                  ...prev,
                  content: { ...prev.content, meta: metaData },
                }))
              }
            />

            <div className='d-flex justify-content-end gap-2'>
              <Button
                variant='secondary'
                onClick={() => setShowFormView(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={isUpdating || isCreating}
              >
                {isUpdating || isCreating ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />
                    {editItem ? "Saving..." : "Adding..."}
                  </>
                ) : editItem ? (
                  "Save Changes"
                ) : (
                  "Add Content"
                )}
              </Button>
            </div>
          </Form>
        </div>
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        isLoading={modalAction === 'delete' ? isDeleting : isToggling}
        title={modalAction === 'delete' ? 'Delete Content' : 'Toggle Status'}
        confirmText={modalAction === 'delete' ? 'Delete Content' : 'Yes'}
        message={
          selectedItem
            ? modalAction === 'delete'
              ? `Are you sure you want to delete "${selectedItem.pageName}"?`
              : `Are you sure you want to ${selectedItem.isActive ? 'deactivate' : 'activate'} "${selectedItem.pageName}"?`
            : `Are you sure you want to ${modalAction === 'delete' ? 'delete' : 'toggle status of'} this item?`
        }
      />
    </div>
  )
}

export default ByDestination
